/**
 * Schedule picker step for multi-schedule businesses.
 *
 * Shared by:
 * - BookingSectionClient (apps/web — live booking)
 * - BookingPreviewContent (apps/dashboard — design preview)
 *
 * Note: The Location model has been removed. Customers now pick a schedule
 * (which may carry an address) rather than a standalone location.
 */
import React from "react";
import Text from "antd/es/typography/Text";
import {
  bookingCardStyle,
  bookingSectionTitleStyle,
  bookingSmallStyle,
} from "./bookingStyles";

export type ScheduleSummary = {
  id: string;
  title: string;
  address?: string;
  image?: string;
  /** The zone the API books this location in (booking window). */
  timezone?: string;
};

/** @deprecated Use {@link ScheduleSummary} instead. */
export type LocationSummary = ScheduleSummary;

export type ScheduleStepProps = {
  schedules: ScheduleSummary[];
  onSelect: (scheduleId: string) => void;
  /** Section heading. Defaults to "Choose a schedule". */
  label?: string;
};

/** @deprecated Use {@link ScheduleStepProps} instead. */
export type LocationStepProps = {
  locations: ScheduleSummary[];
  onSelect: (locationId: string) => void;
};

export function LocationStep(props: ScheduleStepProps | LocationStepProps) {
  const items = "schedules" in props ? props.schedules : props.locations;
  const { onSelect } = props;

  // Auto-select label based on whether items have addresses (physical locations).
  const allHaveAddress =
    items.length > 0 && items.every((i) => i.address?.trim());
  const defaultLabel = allHaveAddress
    ? "Choose a location"
    : "Choose a schedule";
  const label = "label" in props ? (props.label ?? defaultLabel) : defaultLabel;

  return (
    <div>
      <Text
        style={{
          ...bookingSectionTitleStyle,
          marginBottom: 8,
          display: "block",
        }}
      >
        {label}
      </Text>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            style={bookingCardStyle}
            onClick={() => onSelect(item.id)}
          >
            {item.image ? (
              <img
                src={item.image}
                alt={item.title}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <div>
              <p style={{ ...bookingSectionTitleStyle, margin: 0 }}>
                {item.title}
              </p>
              {item.address && (
                <p style={{ ...bookingSmallStyle, margin: 0 }}>
                  {item.address}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
